
import { MicrosoftGraphService } from './microsoftGraphService';

export interface ConnectionHealthStatus {
  isHealthy: boolean;
  lastChecked: Date;
  services: {
    email: ServiceHealth;
    calendar: ServiceHealth;
    profile: ServiceHealth;
  };
  overallStatus: 'healthy' | 'degraded' | 'offline';
  issues: HealthIssue[];
}

export interface ServiceHealth {
  status: 'healthy' | 'degraded' | 'offline';
  responseTime?: number;
  error?: string;
}

export interface HealthIssue {
  service: string;
  severity: 'warning' | 'error';
  message: string;
  suggestedAction?: string;
}

export class ConnectionHealthMonitor {
  private static readonly HEALTH_CHECK_TIMEOUT = 10000; // 10 seconds
  private static readonly MAX_RESPONSE_TIME = 5000; // 5 seconds considered slow

  /**
   * Perform comprehensive health check of Microsoft 365 connection
   */
  static async checkConnectionHealth(userId: string): Promise<ConnectionHealthStatus> {
    const startTime = Date.now();
    const issues: HealthIssue[] = [];
    
    const services = {
      email: await this.checkEmailService(userId),
      calendar: await this.checkCalendarService(userId),
      profile: await this.checkProfileService(userId),
    };

    // Determine overall status
    const serviceStatuses = Object.values(services).map(s => s.status);
    let overallStatus: 'healthy' | 'degraded' | 'offline';
    
    if (serviceStatuses.every(status => status === 'healthy')) {
      overallStatus = 'healthy';
    } else if (serviceStatuses.some(status => status === 'healthy')) {
      overallStatus = 'degraded';
    } else {
      overallStatus = 'offline';
    }

    // Collect issues
    Object.entries(services).forEach(([serviceName, service]) => {
      if (service.status === 'offline') {
        issues.push({
          service: serviceName,
          severity: 'error',
          message: service.error || `${serviceName} service is offline`,
          suggestedAction: 'Try reconnecting your Microsoft account or check your internet connection'
        });
      } else if (service.status === 'degraded') {
        issues.push({
          service: serviceName,
          severity: 'warning',
          message: `${serviceName} service is responding slowly`,
          suggestedAction: 'Performance may be affected. This is usually temporary.'
        });
      }
    });

    return {
      isHealthy: overallStatus === 'healthy',
      lastChecked: new Date(),
      services,
      overallStatus,
      issues,
    };
  }

  private static async checkEmailService(userId: string): Promise<ServiceHealth> {
    try {
      const startTime = Date.now();
      
      // Test basic email connectivity by fetching a small number of messages
      await MicrosoftGraphService.listEmails(userId, { limit: 1 });
      
      const responseTime = Date.now() - startTime;
      
      return {
        status: responseTime > this.MAX_RESPONSE_TIME ? 'degraded' : 'healthy',
        responseTime,
      };
    } catch (error) {
      console.error('Email service health check failed:', error);
      return {
        status: 'offline',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  private static async checkCalendarService(userId: string): Promise<ServiceHealth> {
    try {
      const startTime = Date.now();
      
      // Test calendar connectivity by fetching upcoming events
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 1);
      
      await MicrosoftGraphService.listCalendarEvents(userId, {
        startTime: new Date().toISOString(),
        endTime: endDate.toISOString(),
        limit: 1
      });
      
      const responseTime = Date.now() - startTime;
      
      return {
        status: responseTime > this.MAX_RESPONSE_TIME ? 'degraded' : 'healthy',
        responseTime,
      };
    } catch (error) {
      console.error('Calendar service health check failed:', error);
      return {
        status: 'offline',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  private static async checkProfileService(userId: string): Promise<ServiceHealth> {
    try {
      const startTime = Date.now();
      
      // Test profile connectivity by fetching user info
      await MicrosoftGraphService.getUserProfile(userId);
      
      const responseTime = Date.now() - startTime;
      
      return {
        status: responseTime > this.MAX_RESPONSE_TIME ? 'degraded' : 'healthy',
        responseTime,
      };
    } catch (error) {
      console.error('Profile service health check failed:', error);
      return {
        status: 'offline',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get health status summary for display
   */
  static getHealthSummary(health: ConnectionHealthStatus): {
    icon: string;
    color: string;
    message: string;
  } {
    switch (health.overallStatus) {
      case 'healthy':
        return {
          icon: '✓',
          color: 'text-green-600',
          message: 'All services are operating normally'
        };
      case 'degraded':
        return {
          icon: '⚠',
          color: 'text-yellow-600',
          message: `${health.issues.length} service${health.issues.length > 1 ? 's have' : ' has'} issues`
        };
      case 'offline':
        return {
          icon: '✗',
          color: 'text-red-600',
          message: 'Connection to Microsoft services is unavailable'
        };
      default:
        return {
          icon: '?',
          color: 'text-gray-600',
          message: 'Connection status unknown'
        };
    }
  }
}
